// libs
import { create } from 'zustand';
import dayjs from 'dayjs';
import { IKeywordValidationType } from './defs';
import { INITIAL_STATE } from './constants';
import { KeywordMatchType } from '@local/typedefs/dist/protos/campaign/Criteria';
import { CampaignStatus } from '@local/typedefs/dist/protos/campaign/Campaign';
// helpers
import { isEmpty } from '@/helpers/utils';
const useWriteCampaignStore = create((set, get) => (Object.assign(Object.assign({}, structuredClone(INITIAL_STATE)), { setName: (name) => set({ name }), setStartDate: (date) => {
        set({
            startDate: dayjs(date).startOf('day').toDate(), // campaign starts at 00:00:00 of selected day,
        });
    }, setEndDate: (date) => {
        const endDate = dayjs(date).endOf('day').toDate(); // campaign ends at 23:59:59 of selected day
        /**
         * We need to set milliseconds to 0 because when we convert the date to timestamp on server side
         * it will be converted from 23:59:59:999 to 00:00:00.999 of the next day.
         */
        endDate.setMilliseconds(0);
        set({
            endDate,
        });
    }, setBudget: (budget) => set({ budget }), setCampaignType: (type) => void 0, setPlacementIds: (ids) => set({ placementIds: ids }), setCityIds: (ids) => set({ cityIds: ids.map(Number) }), setPacingEnabled: (enabled) => set({ pacingEnabled: enabled }), setAdGroupName: (name, index = 0) => void 0, setAdGroupUnitType: (type, index = 0) => {
        const adGroups = [...get().adGroups];
        adGroups[index].unitType = type;
        set({ adGroups });
    }, setAdGroupCityIds: (ids, index = 0) => {
        const adGroups = [...get().adGroups];
        adGroups[index].cityIds = ids.map(Number);
        set({ adGroups });
    }, setAdGroupMealSlots: (slots, index = 0) => {
        const adGroups = [...get().adGroups];
        adGroups[index].mealSlots = slots;
        set({ adGroups });
    }, setAdGroupDaysOfWeek: (days, index = 0) => {
        const adGroups = [...get().adGroups];
        adGroups[index].daysOfWeek = days;
        set({ adGroups });
    }, setAdGroupCandidates: (candidates, index = 0) => {
        const newAdGroups = [...get().adGroups];
        newAdGroups[index].candidates = structuredClone(candidates);
        set({ adGroups: newAdGroups });
    }, setAdGroupKeywords: (keywords, index = 0) => set((state) => {
        state.adGroups[index].keywords = keywords;
        return state;
    }), addAdGroupKeyword: (keyword, index = 0) => {
        const newAdGroups = [...get().adGroups];
        newAdGroups[index].keywords = [...newAdGroups[index].keywords, keyword];
        set({ adGroups: newAdGroups });
    }, setKeywordText: (text, keywordIndex, adGroupIndex = 0) => {
        const newAdGroups = [...get().adGroups];
        newAdGroups[adGroupIndex].keywords[keywordIndex].keywordText = text;
    }, setKeywordMatchType: (type, keywordIndex, adGroupIndex = 0) => {
        const newAdGroups = [...get().adGroups];
        newAdGroups[adGroupIndex].keywords[keywordIndex].matchType = type;
        set({ adGroups: newAdGroups });
    }, setKeywordBid: (bid, keywordIndex, adGroupIndex = 0) => {
        const newAdGroups = [...get().adGroups];
        newAdGroups[adGroupIndex].keywords[keywordIndex].bid = bid;
        set({ adGroups: newAdGroups });
    }, updateKeywordByIndex: (keyword, keywordIndex, adGroupIndex = 0) => {
        const adGroups = structuredClone(get().adGroups);
        adGroups[adGroupIndex].keywords[keywordIndex] = Object.assign(Object.assign({}, adGroups[adGroupIndex].keywords[keywordIndex]), keyword);
        return set({ adGroups });
    }, deleteKeywordByIndex: (keywordIndex, adGroupIndex = 0) => {
        const adGroups = structuredClone(get().adGroups);
        adGroups[adGroupIndex].keywords.splice(keywordIndex, 1);
        return set({ adGroups });
    }, clear: () => set(Object.assign({}, structuredClone(INITIAL_STATE))), isValid: (fields, data) => {
        const errorMessages = {};
        const campaign = get();
        for (const key of fields) {
            switch (key) {
                case 'name':
                    if (isEmpty(campaign[key], false)) {
                        errorMessages.name = 'Name is required';
                    }
                    break;
                case 'startDate':
                    if (!campaign[key]) {
                        errorMessages.startDate = 'Start date is required';
                    }
                    break;
                case 'endDate':
                    if (!campaign[key]) {
                        errorMessages.endDate = 'End date is required';
                    }
                    break;
                case 'cityIds':
                    if (isEmpty(campaign[key])) {
                        errorMessages.cityIds = 'City(s) is required';
                    }
                    break;
                case 'mealSlots':
                    if (isEmpty(campaign.adGroups[0][key])) {
                        errorMessages.mealSlots = 'Meal slot(s) is required';
                    }
                    break;
                case 'daysOfWeek':
                    if (isEmpty(campaign.adGroups[0][key])) {
                        errorMessages.daysOfWeek = 'Day(s) of week is required';
                    }
                    break;
                case 'budget':
                    if (isEmpty(campaign[key])) {
                        errorMessages.budget = 'Budget is required';
                    }
                    if (campaign[key] < 100) {
                        errorMessages.budget = 'Budget must greater than 100';
                    }
                    if ((data === null || data === void 0 ? void 0 : data.readCampaign) &&
                        data.readCampaign.status ===
                            CampaignStatus.CAMPAIGN_STATUS_LIVE &&
                        campaign[key] < data.readCampaign[key]) {
                        errorMessages.budget =
                            'Budget cannot be decreased for live campaign';
                    }
                    break;
                case 'candidates':
                    if (isEmpty(campaign.adGroups[0][key])) {
                        errorMessages.candidates = 'Product(s) is required';
                    }
                    break;
                case 'keywords':
                    if (isEmpty(campaign.adGroups[0][key])) {
                        errorMessages.keywords = {
                            errorMessage: {
                                message: 'At least one keyword is required to selected.',
                                type: IKeywordValidationType.INVALID,
                            },
                        };
                    }
                    else {
                        campaign.adGroups[0].keywords.forEach((kw) => {
                            var _a;
                            const kwMetadata = (_a = data === null || data === void 0 ? void 0 : data.keywords) === null || _a === void 0 ? void 0 : _a.find((kwMeta) => kwMeta.keywordText === kw.keywordText);
                            const matchType = kw.matchType;
                            if (kwMetadata &&
                                matchType !==
                                    KeywordMatchType.KEYWORD_MATCH_TYPE_INVALID) {
                                let errMessage = '';
                                let type = IKeywordValidationType.INVALID;
                                if (kwMetadata[matchType].bid
                                    .suggestedLowerBid === 0 &&
                                    kwMetadata[matchType].bid
                                        .suggestedUpperBid === 0) {
                                    errMessage =
                                        'Not a recommended keyword for the products selected';
                                    type =
                                        IKeywordValidationType.ZERO_BID_RANGE;
                                }
                                else if (!kw.bid || kw.bid <= 0) {
                                    errMessage = 'Bid must be greater than 0';
                                }
                                else if (kw.bid <
                                    kwMetadata[matchType].bid.minimumBid) {
                                    errMessage =
                                        'Bid must be greater than minimum bid price';
                                }
                                if (errMessage) {
                                    if (!errorMessages.keywords) {
                                        errorMessages.keywords = {};
                                    }
                                    errorMessages.keywords[kw.keywordText] = {
                                        message: errMessage,
                                        type,
                                    };
                                }
                            }
                        }, []);
                    }
                    break;
                default:
                    break;
            }
        }
        set({ validationMessages: errorMessages });
        return errorMessages;
    } })));
export default useWriteCampaignStore;
